import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiClock, FiDownload, FiLoader } from 'react-icons/fi';
import {
  RegistryExport,
  RegistryMetric,
  RegistryVintages,
  getVintages,
  listRegistryMetrics,
  runExportToCompletion,
} from '../../../api/registry';

/**
 * Download the hour-by-hour record behind a project's results.
 *
 * The Excel button beside this one gives the totals; this gives every hour that
 * produced them, with a per-MWh intensity for each — the grain needed to
 * attach an impact figure to a single MWh (a REC, a settlement interval, an
 * hourly-matching claim).
 *
 * It runs on the same released-vintage machinery as the API: every hour is
 * pinned to the model run that produced it, only released hours are returned,
 * and the file arrives with a manifest that reconciles. So a number pulled here
 * and a number pulled over the API are the same number.
 */

interface Props {
  projectId: string;
  projectName: string;
  /** Null while the session is still resolving; the control hides itself. */
  token: string | null;
}

type Granularity = 'hourly' | 'daily';
type Format = 'csv' | 'parquet';
type MetricChoice = 'all' | 'endpoints' | 'custom';

export function TimeseriesDownloadButton({ projectId, projectName, token }: Props) {
  const [open, setOpen] = useState(false);
  const [vintages, setVintages] = useState<RegistryVintages | null>(null);
  const [metrics, setMetrics] = useState<RegistryMetric[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);

  const [granularity, setGranularity] = useState<Granularity>('hourly');
  const [format, setFormat] = useState<Format>('csv');
  const [metricChoice, setMetricChoice] = useState<MetricChoice>('all');
  const [customKeys, setCustomKeys] = useState<Set<string>>(new Set());
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load what is actually available for THIS generator before offering options,
  // so the panel can show the real released window rather than inviting a
  // request for hours that do not exist yet.
  //
  // Attempt-once, deliberately. An earlier version guarded on `loadingMeta` and
  // listed it as a dependency, so a FAILED request flipped the flag back to
  // false, retriggered the effect, and — because `vintages` never got set —
  // retried forever at network speed. A broken path once produced ~36 req/s and
  // tripped the environment's per-IP rate limit, taking out unrelated traffic
  // from the same egress address. A failure here should cost one request and
  // show a retry control, not become a load generator.
  const attemptedFor = useRef<string | null>(null);

  const loadMeta = useCallback(async () => {
    if (!token) return;
    attemptedFor.current = projectId;
    setLoadingMeta(true);
    setMetaError(null);
    try {
      const [v, m] = await Promise.all([
        getVintages(token, projectId),
        listRegistryMetrics(token),
      ]);
      setVintages(v);
      setMetrics(m);
    } catch (e) {
      setMetaError(
        e instanceof Error ? e.message : 'Could not load export options.',
      );
    } finally {
      setLoadingMeta(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    if (!open || !token) return;
    if (attemptedFor.current === projectId) return; // already tried this one
    void loadMeta();
  }, [open, projectId, token, loadMeta]);

  const released = vintages?.released_through ?? null;
  const firstHour = vintages?.segments?.[0]?.hour_start ?? null;

  const grouped = useMemo(() => {
    const out = new Map<string, RegistryMetric[]>();
    for (const m of metrics) {
      const key = m.family === 'endpoint' ? 'Headline' : m.compartment;
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(m);
    }
    return out;
  }, [metrics]);

  const toggleKey = (key: string) => {
    setCustomKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const download = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    setStatus('Requesting export…');
    try {
      const metricsArg =
        metricChoice === 'all'
          ? undefined
          : metricChoice === 'endpoints'
            ? ['endpoints']
            : Array.from(customKeys);

      if (metricChoice === 'custom' && customKeys.size === 0) {
        throw new Error('Select at least one metric, or choose All metrics.');
      }

      const result: RegistryExport = await runExportToCompletion(
        token,
        {
          project_ids: [projectId],
          granularity,
          format,
          lens: 'operational',
          ...(metricsArg ? { metrics: metricsArg } : {}),
          ...(start ? { start: `${start}T00:00:00Z` } : {}),
          ...(end ? { end: `${end}T00:00:00Z` } : {}),
        },
        {
          onProgress: (s) =>
            setStatus(
              s === 'queued'
                ? 'Queued…'
                : s === 'running'
                  ? 'Building your file…'
                  : 'Finishing…',
            ),
        },
      );

      const file = result.files?.[0];
      if (!file?.download_url) {
        throw new Error('Export completed but no download link was returned.');
      }

      // Navigate to the pre-signed URL rather than fetching it: these files run
      // to tens of megabytes, and streaming to disk via the browser avoids
      // holding the whole thing in memory.
      const a = document.createElement('a');
      a.href = file.download_url;
      a.download = `${projectName.replace(/[^\w.-]+/g, '_')}_timeseries.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setStatus(
        `Downloaded ${file.rows.toLocaleString()} rows (${(file.bytes / 1048576).toFixed(1)} MB).`,
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Export failed.');
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  const btn =
    'inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors border border-quantum-500/50 text-quantum-400 hover:bg-quantum-500/10';

  if (!token) return null;

  if (!open) {
    return (
      <button className={btn} onClick={() => setOpen(true)} type="button">
        <FiClock className="w-4 h-4" />
        Time series
      </button>
    );
  }

  return (
    // Overlay rather than inline: the trigger sits in a header button row
    // (`flex items-center gap-2 shrink-0`), where a full-width panel widens the
    // header past the viewport and makes the page scroll sideways.
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`Download time series for ${projectName}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
    <div className="w-full max-w-xl rounded border border-quantum-500/40 bg-slate-900 p-4 text-sm text-slate-200 shadow-2xl">
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-quantum-300">Download time series</div>
        <button
          className="text-slate-400 hover:text-slate-200 text-xs"
          onClick={() => setOpen(false)}
          type="button"
        >
          Close
        </button>
      </div>

      {loadingMeta && <div className="text-slate-400">Loading options…</div>}
      {metaError && (
        <div className="text-amber-400 flex items-center gap-3">
          <span>{metaError}</span>
          <button
            className="underline text-xs text-quantum-400 hover:text-quantum-300"
            onClick={() => void loadMeta()}
            type="button"
          >
            Try again
          </button>
        </div>
      )}

      {!loadingMeta && !metaError && (
        <>
          {released ? (
            <p className="text-xs text-slate-400 mb-3">
              Released hours available:{' '}
              <span className="text-slate-200">
                {firstHour?.slice(0, 10)} to {released.slice(0, 10)}
              </span>
              {vintages?.readiness?.binding_constraint && (
                <>
                  {' '}· limited by{' '}
                  <span className="text-slate-200">
                    {vintages.readiness.binding_constraint.replace('_', ' ')}
                  </span>
                </>
              )}
              . Values are positive = avoided, UTC hour-beginning.
            </p>
          ) : (
            <p className="text-xs text-amber-400 mb-3">
              No hours have been released for this generator yet, so there is
              nothing to download. Released hours are ones our grid data, model
              coverage and validation all support.
            </p>
          )}

          <div className="mb-3">
            <span className="text-xs text-slate-400">Impact lens</span>
            <p className="text-[11px] text-slate-500 mt-1">
              <span className="text-slate-200">Operational</span> — avoided grid
              operation only. Hours with no fossil generation on the margin come
              back as zero rather than negative, because the project's own
              construction is not amortised across its output.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-xs text-slate-400">Grain</span>
              <select
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
                value={granularity}
                onChange={(e) => setGranularity(e.target.value as Granularity)}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">Format</span>
              <select
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
                value={format}
                onChange={(e) => setFormat(e.target.value as Format)}
              >
                <option value="csv">CSV</option>
                <option value="parquet">Parquet (smaller)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">From (optional)</span>
              <input
                type="date"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">To (optional, exclusive)</span>
              <input
                type="date"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>

          <div className="mb-3">
            <span className="text-xs text-slate-400">Metrics</span>
            <div className="mt-1 flex gap-3 text-xs">
              {(['all', 'endpoints', 'custom'] as MetricChoice[]).map((c) => (
                <label key={c} className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    checked={metricChoice === c}
                    onChange={() => setMetricChoice(c)}
                  />
                  {c === 'all'
                    ? `All (${metrics.length})`
                    : c === 'endpoints'
                      ? 'Headline only (4)'
                      : 'Choose…'}
                </label>
              ))}
            </div>

            {metricChoice === 'custom' && (
              <div className="mt-2 max-h-44 overflow-y-auto border border-slate-700 rounded p-2 space-y-2">
                {Array.from(grouped.entries()).map(([group, items]) => (
                  <div key={group}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                      {group}
                    </div>
                    {items.map((m) => (
                      <label
                        key={m.key}
                        className="flex items-center gap-2 text-xs py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={customKeys.has(m.key)}
                          onChange={() => toggleKey(m.key)}
                        />
                        <span className="text-slate-200">{m.label}</span>
                        <span className="text-slate-500">({m.unit})</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="text-amber-400 text-xs mb-2">{error}</div>}
          {status && !error && (
            <div className="text-slate-400 text-xs mb-2">{status}</div>
          )}

          <button
            className={btn}
            onClick={download}
            disabled={busy || !released}
            type="button"
          >
            {busy ? (
              <FiLoader className="w-4 h-4 animate-spin" />
            ) : (
              <FiDownload className="w-4 h-4" />
            )}
            {busy ? 'Preparing…' : 'Download'}
          </button>
        </>
      )}
    </div>
    </div>
  );
}
